import { useCallback, useMemo, useState } from "react";

type SortDirection = "ASC" | "DESC";

const useSortTable = <T extends Record<string, unknown>>(rows: T[], setPage: (page: number) => void) => {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("ASC");

  const handleSort = useCallback(
    (accessor: string) => {
      if (sortField === accessor) {
        setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
      } else {
        setSortField(accessor);
        setSortDirection("ASC");
      }
      setPage(0);
    },
    [sortField, sortDirection, setPage]
  );

  const sortedRows = useMemo(() => {
    if (!sortField || !rows.length) return rows;

    return [...rows]?.sort((a, b) => {
      const valueA = a[sortField];
      const valueB = b[sortField];

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return sortDirection === "ASC" ? -1 : 1;
      if (valueB == null) return sortDirection === "ASC" ? 1 : -1;

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "ASC" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      }

      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortDirection === "ASC" ? valueA - valueB : valueB - valueA;
      }

      const dateA = valueA instanceof Date ? valueA : new Date(String(valueA));
      const dateB = valueB instanceof Date ? valueB : new Date(String(valueB));
      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        return sortDirection === "ASC" ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
      }

      return sortDirection === "ASC"
        ? String(valueA).localeCompare(String(valueB))
        : String(valueB).localeCompare(String(valueA));
    });
  }, [rows, sortField, sortDirection]);

  return { sortedRows, sortField, sortDirection, handleSort };
};

export default useSortTable;
